import type { ReactNode } from "react";

/**
 * Only an explicit http(s) URL is ever turned into a link.
 *
 * This is the security boundary of this file, not a convenience: the text is
 * free-form and typed by people, and an `href` built from it is NOT escaped by
 * React the way text is. Matching the scheme here is what stops a pasted
 * `javascript:` or `data:` string becoming a clickable anchor. Widening this
 * pattern means re-checking that claim — a bare `www.example.com` is left as
 * plain text on purpose, since guessing a scheme for it is how that guarantee
 * gets lost.
 */
const URL_PATTERN = /https?:\/\/[^\s]+/g;

// Quotes and angle brackets join the sentence punctuation: none of them are
// legal unencoded in a URL, so a trailing one was always the text's, as in
// `see "https://x.com/a"` or `<https://x.com/a>`.
const SENTENCE_TAIL = ".,;:!?\"'>";

/**
 * Drop the punctuation that belongs to the sentence rather than the link.
 *
 * "see https://x.com/a." and "(https://x.com/a)" both end in a character the
 * match swallowed but the URL does not own. A closing bracket is only dropped
 * when it is unbalanced, so a Wikipedia-style URL that genuinely contains one
 * survives.
 */
function trimSentenceTail(url: string): string {
  let end = url.length;
  for (;;) {
    const last = url[end - 1];
    if (last === undefined) break;
    if (SENTENCE_TAIL.includes(last)) {
      end -= 1;
      continue;
    }
    if (last === ")" || last === "]") {
      const open = last === ")" ? "(" : "[";
      const head = url.slice(0, end);
      if (head.split(open).length <= head.split(last).length - 1) {
        end -= 1;
        continue;
      }
    }
    break;
  }
  return url.slice(0, end);
}

/** Split text into plain runs and the links found in it. */
function linkify(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let at = 0;
  for (const match of text.matchAll(URL_PATTERN)) {
    const raw = match[0];
    const start = match.index;
    const href = trimSentenceTail(raw);
    // An all-punctuation tail can leave nothing worth linking.
    if (href === "") continue;
    if (start > at) out.push(text.slice(at, start));
    out.push(
      <a
        key={`${start}-${href}`}
        href={href}
        target="_blank"
        rel="noreferrer"
        className="wrap-anywhere text-brand-600 hover:text-brand-700 focus-visible:focus-ring"
      >
        {href}
      </a>,
    );
    at = start + href.length;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}

/**
 * A read-only block of someone's free text: the account brief, a request's
 * description, a desired outcome.
 *
 * It owns the three things that kind of text needs and a bare <p> does not.
 * pre-line keeps the blank lines people use to group notes, which HTML would
 * otherwise collapse; break-words stops a pasted URL — one unbreakable token —
 * from running past its panel and being clipped; and linkify makes those URLs
 * clickable rather than something to copy out by hand.
 */
export function RichText({ text }: { text: string }) {
  return <p className="whitespace-pre-line break-words px-4 py-4 text-sm text-gray-800">{linkify(text)}</p>;
}
